/* ═══════════════════════════════════════════════════════════════
   PiARIS — Audit Filters
   Filter bar: priority (P1/P2/P3) · category · sitewide only ·
   high-risk only · search. All via controlled props.
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { P, F } from "../constants";

var E = React.createElement;

var PRIORITIES = [
  { id: "all", label: "All" },
  { id: "P1",  label: "Critical (P1)", color: "sCrit" },
  { id: "P2",  label: "Serious (P2)",  color: "amber" },
  { id: "P3",  label: "Warning (P3)",  color: "emerald" }
];

/** Filter toggle button */
function FilterPill(p) {
  var col = p.activeColor ? P[p.activeColor] : P.pri;
  var bg  = p.activeColor === "sCrit" ? P.sCritBg
         : p.activeColor === "amber" ? P.sMajBg
         : p.activeColor === "emerald" ? P.emeraldLt
         : P.priLt;
  return E("button", {
    type: "button",
    onClick: p.onClick,
    "aria-pressed": p.active ? "true" : "false",
    style: {
      minHeight: 36,
      padding: "6px 14px",
      borderRadius: 8,
      border: "1.5px solid " + (p.active ? col : P.bdr),
      background: p.active ? bg : P.card,
      color: p.active ? col : P.slateM,
      fontSize: 11,
      fontWeight: p.active ? 800 : 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
      fontFamily: "inherit",
      transition: "all .12s"
    }
  }, p.children);
}

/** AuditFilters — props:
    priority, onPriority, sitewideOnly, onSitewide,
    highRiskOnly, onHighRisk, search, onSearch,
    totalShown, totalAll */
function AuditFilters(p) {
  return E("div", {
    role: "toolbar",
    "aria-label": "Audit report filters",
    style: {
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
      alignItems: "center",
      padding: "12px 16px",
      background: P.bgS,
      border: "1px solid " + P.bdr,
      borderRadius: 12,
      marginBottom: 16
    }
  },
    /* Search box */
    E("input", {
      type: "search",
      placeholder: "\uD83D\uDD0D Search issues\u2026",
      value: p.search || "",
      onChange: function(ev) { p.onSearch && p.onSearch(ev.target.value); },
      "aria-label": "Search issues by title or element",
      style: {
        minHeight: 36,
        padding: "6px 12px",
        borderRadius: 8,
        border: "1.5px solid " + P.bdr,
        background: P.card,
        color: P.slate,
        fontSize: 11,
        fontFamily: F,
        outline: "none",
        flex: "1 1 200px",
        minWidth: 160
      }
    }),
    /* Priority filter group */
    E("div", { style: { display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" } },
      E("span", {
        style: { fontSize: 9, fontWeight: 800, color: P.slateL, textTransform: "uppercase", letterSpacing: ".04em", marginRight: 2 }
      }, "Priority:"),
      PRIORITIES.map(function(pri) {
        var active = (p.priority || "all") === pri.id;
        return E(FilterPill, {
          key: pri.id,
          active: active,
          activeColor: pri.color,
          onClick: function() { p.onPriority && p.onPriority(pri.id); }
        }, pri.label);
      })
    ),
    /* Sitewide toggle */
    E(FilterPill, {
      active: !!p.sitewideOnly,
      activeColor: "pri",
      onClick: function() { p.onSitewide && p.onSitewide(!p.sitewideOnly); }
    }, "\uD83C\uDF10 Sitewide only"),
    /* High-risk toggle */
    E(FilterPill, {
      active: !!p.highRiskOnly,
      activeColor: "sCrit",
      onClick: function() { p.onHighRisk && p.onHighRisk(!p.highRiskOnly); }
    }, "\u26A0 High risk only"),
    /* Open only (hide remediated) */
    E(FilterPill, {
      active: !!p.openOnly,
      activeColor: "emerald",
      onClick: function() { p.onOpenOnly && p.onOpenOnly(!p.openOnly); }
    }, "Open only"),
    /* Result count */
    p.totalAll != null ? E("div", {
      "aria-live": "polite",
      style: {
        marginLeft: "auto",
        fontSize: 11,
        color: P.slateM,
        fontWeight: 600,
        fontFeatureSettings: '"tnum"'
      }
    }, "Showing " + (p.totalShown || 0).toLocaleString() + " of " + p.totalAll.toLocaleString() + " issues") : null
  );
}

export { AuditFilters };
